/**
 * Webtech HR Engine™ - Skill Evaluation Campaigns
 * Gestion des campagnes d'évaluation des compétences
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowLeft,
  Plus,
  Search,
  Target,
  MoreVertical,
  Play,
  Pause,
  Eye,
  Trash2,
  Loader2,
  Calendar,
  Users,
  CheckCircle2,
  Clock,
  AlertCircle,
  Brain,
  UserCheck,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: Clock },
  active: { label: 'Active', color: 'bg-green-100 text-green-700', icon: Play },
  closed: { label: 'Clôturée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
};

export default function SkillCampaignsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, campaign: null });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    targetType: 'all',
    targetDepartments: [],
    targetEmployees: [],
    evaluationModes: ['self', 'manager']
  });
  
  // Employees for manual selection
  const [employees, setEmployees] = useState([]);
  const [employeeSearch, setEmployeeSearch] = useState('');

  useEffect(() => {
    if (companyId) loadData();
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [campaignsRes, deptRes, empRes] = await Promise.all([
        api.get(`/skills/campaigns?company_id=${companyId}`),
        api.get('/departments'),
        api.get(`/employees?company_id=${companyId}`)
      ]);
      setCampaigns(campaignsRes.data.campaigns || []);
      setDepartments(deptRes.data.departments || deptRes.data || []);
      setEmployees(empRes.data.employees || empRes.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (formData.targetType === 'department' && formData.targetDepartments.length === 0) {
      toast.error('Veuillez sélectionner au moins un département');
      return;
    }
    
    if (formData.targetType === 'custom' && formData.targetEmployees.length === 0) {
      toast.error('Veuillez sélectionner au moins un employé');
      return;
    }
    
    try {
      await api.post(`/skills/campaigns?company_id=${companyId}`, formData);
      toast.success('Campagne créée');
      setCreateDialog(false);
      setFormData({
        name: '',
        description: '',
        startDate: '',
        endDate: '',
        targetType: 'all',
        targetDepartments: [],
        targetEmployees: [],
        evaluationModes: ['self', 'manager']
      });
      setEmployeeSearch('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const handleActivate = async (campaignId) => {
    try {
      const res = await api.post(`/skills/campaigns/${campaignId}/activate?company_id=${companyId}`);
      toast.success(`Campagne activée avec ${res.data.participantsCreated} participants`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'activation');
    }
  };

  const handleClose = async (campaignId) => {
    try {
      await api.post(`/skills/campaigns/${campaignId}/close?company_id=${companyId}`);
      toast.success('Campagne clôturée');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la clôture');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/skills/campaigns/${deleteDialog.campaign.id}?company_id=${companyId}`);
      toast.success('Campagne supprimée');
      setDeleteDialog({ open: false, campaign: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const toggleDepartment = (deptId) => {
    setFormData(prev => ({
      ...prev,
      targetDepartments: prev.targetDepartments.includes(deptId)
        ? prev.targetDepartments.filter(id => id !== deptId)
        : [...prev.targetDepartments, deptId]
    }));
  };

  const toggleEmployee = (empId) => {
    setFormData(prev => ({
      ...prev,
      targetEmployees: prev.targetEmployees.includes(empId)
        ? prev.targetEmployees.filter(id => id !== empId)
        : [...prev.targetEmployees, empId]
    }));
  };

  const selectAllEmployees = () => {
    const activeEmployees = employees.filter(e => e.status === 'Actif');
    setFormData(prev => ({
      ...prev,
      targetEmployees: activeEmployees.map(e => e.id)
    }));
  };

  const clearAllEmployees = () => {
    setFormData(prev => ({
      ...prev,
      targetEmployees: []
    }));
  };

  // Filter employees for search
  const filteredEmployees = employees.filter(e => {
    if (e.status !== 'Actif') return false;
    if (!employeeSearch) return true;
    const search = employeeSearch.toLowerCase();
    return (
      e.firstName?.toLowerCase().includes(search) ||
      e.lastName?.toLowerCase().includes(search) ||
      e.email?.toLowerCase().includes(search) ||
      e.jobTitle?.toLowerCase().includes(search)
    );
  });

  const filteredCampaigns = campaigns.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || c.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const isAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  return (
    <DashboardLayout title="Campagnes d'évaluation">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate('/skills')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Retour
            </Button>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Target className="h-7 w-7 text-webtech-blue" />
              Campagnes d'évaluation
            </h1>
            <p className="text-slate-500 mt-1">Gérez les campagnes d'évaluation des compétences</p>
          </div>
          
          {isAdmin && (
            <Button onClick={() => setCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle campagne
            </Button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{campaigns.length}</p>
                </div>
                <Target className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Actives</p>
                  <p className="text-2xl font-bold text-green-600">
                    {campaigns.filter(c => c.status === 'active').length}
                  </p>
                </div>
                <Play className="h-8 w-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Brouillons</p>
                  <p className="text-2xl font-bold text-slate-600">
                    {campaigns.filter(c => c.status === 'draft').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Clôturées</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {campaigns.filter(c => c.status === 'closed').length}
                  </p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="draft">Brouillon</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Clôturée</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Campaigns List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
          </div>
        ) : campaigns.length === 0 ? (
          <Card className="py-12">
            <CardContent className="text-center">
              <Target className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucune campagne</h3>
              <p className="text-slate-500 mb-6">Créez une campagne pour évaluer les compétences.</p>
              {isAdmin && (
                <Button onClick={() => setCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une campagne
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredCampaigns.map(campaign => {
              const config = STATUS_CONFIG[campaign.status] || STATUS_CONFIG.draft;
              const StatusIcon = config.icon;
              
              return (
                <Card key={campaign.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={config.color}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {config.label}
                          </Badge>
                        </div>
                        <CardTitle className="text-base">{campaign.name}</CardTitle>
                      </div>
                      
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/skills/campaigns/${campaign.id}`)}>
                              <Eye className="h-4 w-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            {campaign.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleActivate(campaign.id)}>
                                <Play className="h-4 w-4 mr-2" />
                                Activer
                              </DropdownMenuItem>
                            )}
                            {campaign.status === 'active' && (
                              <DropdownMenuItem onClick={() => handleClose(campaign.id)}>
                                <Pause className="h-4 w-4 mr-2" />
                                Clôturer
                              </DropdownMenuItem>
                            )}
                            {campaign.status !== 'active' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-red-600"
                                  onClick={() => setDeleteDialog({ open: true, campaign })}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {campaign.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2">{campaign.description}</p>
                    )}
                    
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(campaign.startDate).toLocaleDateString('fr-FR')} - {new Date(campaign.endDate).toLocaleDateString('fr-FR')}</span>
                      </div>
                      
                      {campaign.stats && (
                        <>
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Users className="h-4 w-4" />
                            <span>{campaign.stats.totalParticipants} participants</span>
                          </div>
                          
                          <div>
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-500">Progression</span>
                              <span className="font-medium">{campaign.stats.progress}%</span>
                            </div>
                            <Progress value={campaign.stats.progress} className="h-2" />
                          </div>
                        </>
                      )}
                    </div>
                    
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate(`/skills/campaigns/${campaign.id}`)}
                    >
                      Voir détails
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle campagne d'évaluation</DialogTitle>
            <DialogDescription>
              Créez une campagne pour évaluer les compétences de vos collaborateurs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Nom de la campagne *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Évaluation annuelle 2025"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la campagne..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Date de fin *</Label>
                <Input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>
            
            <div>
              <Label>Ciblage</Label>
              <Select 
                value={formData.targetType} 
                onValueChange={(v) => setFormData({ ...formData, targetType: v, targetDepartments: [], targetEmployees: [] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les employés</SelectItem>
                  <SelectItem value="department">Par département</SelectItem>
                  <SelectItem value="custom">Sélection manuelle</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {formData.targetType === 'department' && (
              <div>
                <Label>Départements ciblés</Label>
                <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {departments.map(dept => (
                    <div key={dept.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.targetDepartments.includes(dept.id)}
                        onCheckedChange={() => toggleDepartment(dept.id)}
                      />
                      <span className="text-sm">{dept.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {formData.targetType === 'custom' && (
              <div>
                <Label>Employés ciblés ({formData.targetEmployees.length} sélectionné(s))</Label>
                
                {/* Selected employees as badges */}
                {formData.targetEmployees.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2 mb-3 p-2 bg-slate-50 rounded-md border">
                    {formData.targetEmployees.map(empId => {
                      const emp = employees.find(e => e.id === empId);
                      return emp ? (
                        <Badge key={empId} variant="secondary" className="flex items-center gap-1 py-1 px-2">
                          <span className="h-5 w-5 rounded-full bg-webtech-blue flex items-center justify-center text-white text-[10px] font-medium">
                            {emp.firstName?.[0]}{emp.lastName?.[0]}
                          </span>
                          <span className="text-xs">{emp.firstName} {emp.lastName}</span>
                          <X 
                            className="h-3 w-3 cursor-pointer hover:text-red-500 ml-1" 
                            onClick={() => toggleEmployee(empId)}
                          />
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                
                {/* Search input with dropdown */}
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Tapez pour rechercher un employé..."
                      value={employeeSearch}
                      onChange={(e) => setEmployeeSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  
                  {/* Dropdown results - only show when typing */}
                  {employeeSearch.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredEmployees.length === 0 ? (
                        <p className="p-3 text-sm text-slate-500 text-center">Aucun employé trouvé</p>
                      ) : (
                        filteredEmployees.slice(0, 10).map(emp => {
                          const isSelected = formData.targetEmployees.includes(emp.id);
                          return (
                            <div 
                              key={emp.id} 
                              className={`flex items-center gap-3 p-2 hover:bg-slate-50 cursor-pointer border-b last:border-b-0 ${
                                isSelected ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => {
                                toggleEmployee(emp.id);
                                if (!isSelected) {
                                  setEmployeeSearch(''); // Clear search after selection
                                }
                              }}
                            >
                              <div className="h-8 w-8 rounded-full bg-webtech-blue flex items-center justify-center text-white text-xs font-medium">
                                {emp.firstName?.[0]}{emp.lastName?.[0]}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">
                                  {emp.firstName} {emp.lastName}
                                </p>
                                <p className="text-xs text-slate-500 truncate">{emp.jobTitle || emp.position || 'Non défini'}</p>
                              </div>
                              {isSelected ? (
                                <UserCheck className="h-4 w-4 text-green-500" />
                              ) : (
                                <Plus className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                          );
                        })
                      )}
                      {filteredEmployees.length > 10 && (
                        <p className="p-2 text-xs text-slate-500 text-center bg-slate-50 border-t">
                          +{filteredEmployees.length - 10} autres résultats, affinez votre recherche
                        </p>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Help text */}
                <p className="text-xs text-slate-500 mt-2">
                  Commencez à taper pour rechercher et sélectionner des employés
                </p>
              </div>
            )}
            
            <div>
              <Label>Modes d'évaluation</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.evaluationModes.includes('self')}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({
                        ...prev,
                        evaluationModes: checked
                          ? [...prev.evaluationModes, 'self']
                          : prev.evaluationModes.filter(m => m !== 'self')
                      }));
                    }}
                  />
                  <span className="text-sm">Auto-évaluation</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.evaluationModes.includes('manager')}
                    onCheckedChange={(checked) => {
                      setFormData(prev => ({
                        ...prev,
                        evaluationModes: checked
                          ? [...prev.evaluationModes, 'manager']
                          : prev.evaluationModes.filter(m => m !== 'manager')
                      }));
                    }}
                  />
                  <span className="text-sm">Évaluation manager</span>
                </label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreate}>
              Créer la campagne
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Supprimer la campagne</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer la campagne <strong>"{deleteDialog.campaign?.name}"</strong> ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
